import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import LoadingButton from '../common/LoadingButton';
import {
  closeMultiPoliciesModal,
  paymentMethodPastDueModalVisibleChanged,
} from '../../actions';
import { ModalContainer } from '../common';
import FigoLoadingOverlay from '../common/FigoLoadingOverlay';
import closeIcon from '../../assets/close-button.svg';
import './EditPaymentMultiPolicyModal.css';

const EditPaymentMultiPolicyModal = ({
  hasPastPaymentDue = false,
  onUpdatePaymentMethod,
}) => {
  const { t } = useTranslation('settingsScreen');
  const dispatch = useDispatch();
  const store = useSelector(({ editPayment, billingPayments }) => ({
    billingPayments,
    editPayment,
  }));
  const {
    billingPayments: {
      updatingPaymentMethod,
    },
    editPayment: { paymentMethodData, policiesList },
  } = store;

  const modalVisible = useMemo(() => store.editPayment
    .isVisible, [store.editPayment.isVisible]);

  const onCancel = useCallback(() => {
    if (!updatingPaymentMethod) {
      dispatch(closeMultiPoliciesModal());
    }
  }, [updatingPaymentMethod, dispatch]);

  const onSave = useCallback(() => {
    const {
      policiesToUpdate,
      sameBillingAddress,
      userInformation,
    } = store.editPayment;

    if (updatingPaymentMethod) {
      return;
    }

    const extraPolicies = policiesList.filter((policy) => (
      policy.isToUpdate
    )).map((item) => (item.Policy.Number));

    const requestUpdatePaymentMethod = {
      paymentMethodData,
      policies: [...policiesToUpdate, ...extraPolicies],
      PolicyNumber: policiesToUpdate[0],
      sameBillingAddress,
      userInformation,
    };

    if (hasPastPaymentDue) {
      dispatch(closeMultiPoliciesModal());
      dispatch(paymentMethodPastDueModalVisibleChanged({
        requestUpdatePaymentMethod,
        visible: true,
      }));
      return;
    }

    onUpdatePaymentMethod(requestUpdatePaymentMethod);
  }, [
    dispatch,
    hasPastPaymentDue,
    onUpdatePaymentMethod,
    paymentMethodData,
    policiesList,
    store.editPayment,
    updatingPaymentMethod,
  ]);

  if (updatingPaymentMethod) {
    return <FigoLoadingOverlay visible />;
  }

  if (!modalVisible) {
    return null;
  }

  return (
    <ModalContainer
      contentStyle="Edit-payment-modal"
      handleOutClick={onCancel}
      show
    >
      <button
        className="Edit-payment-policy-withoulist-close-button"
        onClick={onCancel}
        type="button"
      >
        <img alt="close" src={closeIcon} />
      </button>

      <div className="Edit-payment-policy-withoutlist-container">
        <span className="Edit-payment-withoutlist-title-text">
          {t('editPoliciesPaymentMethod.title')}
        </span>
      </div>

      <div className="Edit-payment-withoutlist-description-container">
        <p className="Edit-payment-withoutlist-description-text">
          {t('editPoliciesPaymentMethod.withoutListDescription')}
        </p>
      </div>

      <div className="Edit-payment-withoutlist-buttons-container">
        <LoadingButton
          className="Edit-payment-button-update"
          disabled={updatingPaymentMethod}
          id="saveMultiplePoliciesPayMethod"
          loading={updatingPaymentMethod}
          onClick={onSave}
          text={t('editPoliciesPaymentMethod.update')}
          textStyle="Edit-payment-button-update-text"
        />

        <button
          className="Edit-payment-button-cancel"
          disabled={updatingPaymentMethod}
          id="cancelMultiplePoliciesPayMethod"
          onClick={onCancel}
          type="button"
        >
          <span className="Edit-payment-button-cancel-text">
            {t('editPoliciesPaymentMethod.cancel')}
          </span>
        </button>
      </div>
    </ModalContainer>
  );
};

export { EditPaymentMultiPolicyModal };
